import { httpClient } from '@/services/http/httpClient'
import type { Option } from '@/types/option'
import { getQueryParams, type QueryParams } from '@/utils/queryParams'

import type { IdeaStatus } from '../types'

type MyIdeaHttpResponse = {
  id: string
  title: string
  description: string
  sequence: string
  ideaStatuses: {
    name: string
    customName: string | null
    color: string
    customColor: string | null
  }
  likes: number
  view: number
  ideaUsers: {
    user: {
      id: string
      name: string
      email: string
      image?: string
      area?: {
        id: string
        name: string
      }
    }
  }[]
  ideaSteps: {
    id: string
    completed: boolean
  }[]
  processActivity?: {
    id: string
    name: string
  }
  secondaryLinks: {
    id: string
    sequence: string
    title: string
  }[]
  campaign: {
    id: string
    sequence: number
    title: string
  }
}

type MyIdeasHttpResponse = {
  ideas: MyIdeaHttpResponse[]
  paginate: {
    total: number
    page: number
    pages: number
    limit: number
  }
}

type MyIdea = {
  id: string
  sequence: string
  title: string
  description: string
  status: IdeaStatus
  statusColor: string
  likes: number
  views: number
  linkedIdeas: (Option & { title: string })[]
  workflow?: { name: string; totalStages: number; currentStage: number }
  participants: {
    id: string
    name: string
    email: string
    image?: string
    area?: {
      id: string
      name: string
    }
  }[]
  challenge: {
    id: string
    sequence: string
    title: string
  }
}

type MyIdeasParams = {
  query: QueryParams
}

type MyIdeasResponse = {
  ideas: MyIdea[]
  pagination: {
    total: number
    page: number
    pages: number
    limit: number
  }
}

export async function getMyIdeas({
  query,
}: MyIdeasParams): Promise<MyIdeasResponse> {
  const queryParams = getQueryParams(query)

  const { data } = await httpClient.get<MyIdeasHttpResponse>(
    `/ideas/ideas/me?${queryParams}`,
  )

  return {
    ideas: data.ideas.map((idea) => {
      const workflow = {
        name: idea.processActivity?.name,
        totalStages: idea.ideaSteps?.length ?? 0,
        currentStage:
          idea.ideaSteps.filter((step) => step.completed).length + 1,
      }

      return {
        id: idea.id,
        sequence: idea.sequence,
        title: idea.title,
        description: idea.description,
        status: idea.ideaStatuses.name as IdeaStatus,
        statusColor: idea.ideaStatuses.customColor ?? idea.ideaStatuses.color,
        likes: idea.likes,
        views: idea.view,
        ...(idea.processActivity && { workflow }),
        participants: idea.ideaUsers.map((ideaUser) => ({
          id: ideaUser.user.id,
          name: ideaUser.user.name,
          email: ideaUser.user.email,
          image: ideaUser.user.image,
          area: ideaUser.user.area
            ? {
                id: ideaUser.user.area.id,
                name: ideaUser.user.area.name,
              }
            : undefined,
        })),
        linkedIdeas: idea.secondaryLinks.map((secondaryLink) => ({
          value: secondaryLink.id,
          label: `#${String(secondaryLink.sequence).padStart(6, '0')}`,
          title: secondaryLink.title,
        })),
        challenge: {
          id: idea.campaign.id,
          sequence: `#${String(idea.campaign.sequence).padStart(2, '0')}`,
          title: idea.campaign.title,
        },
      } as MyIdea
    }),
    pagination: {
      total: data.paginate.total,
      page: data.paginate.page,
      pages: data.paginate.pages,
      limit: data.paginate.limit,
    },
  }
}
